import { Button } from '@mui/material';
import Box from '@mui/material/Box';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import { QueryStatus } from '@reduxjs/toolkit/query';
import React from 'react';

import TrashCanIcon from '@/assets/icons/trash.svg?react';
import { DocumentPreviewWidget } from '@/components/molecules/DocumentPreviewWidget';
import { useTranslator } from '@/hooks/useTranslator';
import { FileInterface } from '@/types/fileInterface';

interface Props {
  readonly name?: string;
  readonly buttonTitle?: string;
  readonly accept?: string;
  readonly value: FileInterface | null;
  readonly error: string | null;
  readonly status: QueryStatus;

  onChange(event: React.ChangeEvent<HTMLInputElement>): void;
  onRemove(): void;
}

export const FileInput: React.FC<Props> = (props: Props): React.ReactElement => {
  const tr = useTranslator();
  const {
    name,
    accept,
    value,
    error,
    status,
    onChange,
    onRemove,
    buttonTitle = tr('UPLOAD_DOCUMENT'),
  } = props;

  const [fileInput, setFileInput] = React.useState<HTMLInputElement | null>(null);

  const onClick = React.useCallback((): void => {
    fileInput?.click();
  }, [fileInput]);

  const deleteFile = React.useCallback((): void => {
    if (fileInput) {
      fileInput.value = '';
    }
    onRemove?.();
  }, [fileInput, onRemove]);

  return (
    <Box sx={styles.container}>
      <input
        ref={setFileInput}
        type="file"
        accept={accept}
        style={fileInputStyle}
        onChange={onChange}
      />
      {status === QueryStatus.pending ? (
        <DocumentPreviewWidget
          name=""
          icon={TrashCanIcon}
          size={null}
          status={QueryStatus.pending}
        />
      ) : value ? (
        <DocumentPreviewWidget
          name={value.name}
          icon={TrashCanIcon}
          size={value.size}
          status={status}
          error={error}
          onIconClick={deleteFile}
        />
      ) : (
        <Button variant="contained" sx={styles.button} id={name} onClick={onClick}>
          {buttonTitle}
        </Button>
      )}
    </Box>
  );
};

const styles: Record<string, SystemStyleObject> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  currentFileBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: 'custom.OffWhite',
    borderColor: 'custom.DividerMed',
    borderWidth: 1,
    borderStyle: 'solid',
    py: 2,
    pl: 3,
    pr: 2,
    borderRadius: 4,
    position: 'relative',
  },
  button: {
    backgroundColor: 'custom.BlackLightBackground',
  },
};

const fileInputStyle: React.CSSProperties = { display: 'none' };
